import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Dialog, DialogContent } from '@mui/material';
import StimulusEditor from './StimulusEditor';
import { normalizeStimulusText } from './stimulusHelpers';
import SavingIcon from '../../static/icons/saving-icon.svg';

const StimulusModal = ({
  open,
  onClose,
  initialStimulusText,
  initialStimulusFiles,
  onSave,
}) => {
  const [stimulusText, setStimulusText] = useState('');
  const [stimulusFiles, setStimulusFiles] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const wasOpen = useRef(false);

  useEffect(() => {
    if (open && !wasOpen.current) {
      setStimulusText(normalizeStimulusText(initialStimulusText || ''));
      setStimulusFiles(initialStimulusFiles || []);
      setIsSaving(false);
      setError('');
    }
    wasOpen.current = open;
  }, [open, initialStimulusText, initialStimulusFiles]);

  const handleClose = () => {
    if (!isSaving) {
      onClose?.();
    }
  };

  const handleSave = async () => {
    if (isSaving) {
      return;
    }
    try {
      setIsSaving(true);
      setError('');
      await onSave?.({
        stimulusText: normalizeStimulusText(stimulusText),
        stimulusFiles,
      });
      onClose?.();
    } catch (error) {
      setIsSaving(false);
      setError('The stimulus could not be saved. Your changes are still here. Please retry.');
    }
  };

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth={false}>
      <StyledDialogContent>
        <ModalContainer>
          <ModalHeader>
            <HeaderTitle>Add Stimulus</HeaderTitle>
            <HeaderRight>
              {isSaving && (
                <SavingIndicator>
                  <SavingIconImg src={SavingIcon} alt="Saving" />
                  Saving
                </SavingIndicator>
              )}
              <CloseButton onClick={handleClose}>×</CloseButton>
            </HeaderRight>
          </ModalHeader>

          <ModalBody>
            {error && <p role="alert">{error}</p>}
            <StimulusEditor
              stimulusText={stimulusText}
              stimulusFiles={stimulusFiles}
              onStimulusTextChange={setStimulusText}
              onStimulusFilesChange={setStimulusFiles}
            />
          </ModalBody>

          <ModalFooter>
            <ContinueButton onClick={handleSave} disabled={isSaving}>
              Continue
            </ContinueButton>
          </ModalFooter>
        </ModalContainer>
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default StimulusModal;

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    background-color: transparent;
    box-shadow: none;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  padding: 0;
  overflow: visible;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 880px;
  max-width: 95vw;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  font-family: var(--font-family-ibm_plex_sans);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fbf7fe;
  padding: 16px 24px;
`;

const HeaderTitle = styled.h2`
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 20px;
  font-weight: 600;
  color: var(--text);
  margin: 0;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SavingIndicator = styled.span`
  font-size: 14px;
  color: #8b8b8b;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const SavingIconImg = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
  display: block;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBody = styled.div`
  padding: 16px 24px;
  overflow-y: auto;
  flex: 1;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px 24px;
`;

const ContinueButton = styled.button`
  padding: 10px 24px;
  border-radius: 999px;
  border: none;
  background: #7200e0;
  color: #fff;
  font-family: var(--font-family-ibm_plex_sans);
  font-weight: 600;
  cursor: pointer;
`;
